import { useState, useRef, useEffect } from 'react'
import { Link } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { Settings, Key, Building2, LogOut, ChevronDown, Shield, Wallet } from 'lucide-react'
import { cn } from '@/lib/utils'

interface UserDropdownUser { username: string; email?: string; is_admin?: boolean; is_workspace_owner?: boolean; can_manage_credits?: boolean }
interface UserDropdownProps { user: UserDropdownUser; onLogout: () => void }

function getInitials(name: string): string { return name.split(' ').map(p => p[0]).join('').toUpperCase().slice(0, 2) || 'U' }

export function UserDropdown({ user, onLogout }: UserDropdownProps) {
  const { t } = useTranslation()
  const [open, setOpen] = useState(false)
  const ref = useRef<HTMLDivElement>(null)

  useEffect(() => {
    const handler = (e: MouseEvent) => { if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false) }
    document.addEventListener('mousedown', handler)
    return () => document.removeEventListener('mousedown', handler)
  }, [])

  useEffect(() => {
    const handler = (e: KeyboardEvent) => { if (e.key === 'Escape') setOpen(false) }
    document.addEventListener('keydown', handler)
    return () => document.removeEventListener('keydown', handler)
  }, [])

  const close = () => setOpen(false)
  const initials = getInitials(user.username)
  const menuItemClass = "flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm text-neutral-800 dark:text-neutral-200 hover:text-neutral-900 dark:hover:text-neutral-100 hover:bg-neutral-100 dark:hover:bg-neutral-800 transition-colors"
  const iconBoxClass = "flex items-center justify-center w-8 h-8 rounded-lg"
  const iconBoxNeutral = cn(iconBoxClass, "bg-neutral-200 dark:bg-neutral-700 text-neutral-700 dark:text-neutral-300")

  return (
    <div className="relative" ref={ref}>
      <button onClick={() => setOpen(!open)} className={cn(
        "flex items-center gap-2.5 px-2 py-1.5 rounded-lg text-sm font-semibold transition-all duration-200",
        "hover:bg-neutral-100 dark:hover:bg-neutral-800 focus-visible:ring-2 focus-visible:ring-primary-500 focus-visible:ring-offset-2 dark:focus-visible:ring-offset-neutral-900",
        open ? "bg-neutral-100 dark:bg-neutral-800" : ""
      )} aria-expanded={open} aria-haspopup="true">
        <div className="flex items-center justify-center w-8 h-8 rounded-full bg-primary-600 text-white text-xs font-bold shadow-sm">{initials}</div>
        <span className="hidden sm:block text-neutral-800 dark:text-neutral-200 max-w-[120px] truncate">{user.username}</span>
        <ChevronDown className={cn("h-4 w-4 text-neutral-600 dark:text-neutral-400 transition-transform duration-200", open && "rotate-180")} />
      </button>

      <div className={cn(
        "absolute right-0 mt-2 w-72 z-50 bg-white dark:bg-neutral-900 border border-neutral-200 dark:border-neutral-700 rounded-xl shadow-xl",
        "transition-all duration-200 origin-top-right",
        open ? "opacity-100 scale-100 translate-y-0" : "opacity-0 scale-95 -translate-y-2 pointer-events-none"
      )}>
        <div className="p-4 border-b border-neutral-200 dark:border-neutral-700">
          <div className="flex items-center gap-3">
            <div className="flex items-center justify-center w-12 h-12 rounded-full bg-primary-600 text-white text-sm font-bold shadow-md">{initials}</div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-bold text-neutral-900 dark:text-neutral-100 truncate">{user.username}</p>
              <p className="text-xs text-neutral-600 dark:text-neutral-400 truncate">{user.email || t('nav.noEmailSet')}</p>
              {user.is_admin && (
                <span className="inline-flex items-center gap-1 mt-1 px-1.5 py-0.5 text-2xs bg-red-100 text-red-700 rounded font-semibold">
                  <Shield className="h-3 w-3" />{t('nav.administrator')}
                </span>
              )}
            </div>
          </div>
        </div>
        <div className="p-2">
          <Link to="/v2/settings" onClick={close} className={menuItemClass}>
            <div className={iconBoxNeutral}><Settings className="h-4 w-4" /></div>
            <div><p className="font-semibold">{t('nav.settings')}</p><p className="text-xs text-neutral-600 dark:text-neutral-400">{t('nav.profileAndPreferences')}</p></div>
          </Link>
          {user.can_manage_credits && (
            <Link to="/v2/credits" onClick={close} className={menuItemClass}>
              <div className={cn(iconBoxClass, "bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-300")}><Wallet className="h-4 w-4" /></div>
              <div><p className="font-semibold">{t('nav.creditsAndBilling')}</p><p className="text-xs text-neutral-600 dark:text-neutral-400">{t('nav.creditsAndBillingDesc')}</p></div>
            </Link>
          )}
          {user.can_manage_credits && (
            <Link to="/api-tokens" onClick={close} className={menuItemClass}>
              <div className={iconBoxNeutral}><Key className="h-4 w-4" /></div>
              <div><p className="font-semibold">{t('nav.apiTokens')}</p><p className="text-xs text-neutral-600 dark:text-neutral-400">{t('nav.manageApiTokens')}</p></div>
            </Link>
          )}
          {user.is_workspace_owner && (
            <>
              <div className="my-2 mx-3 border-t border-neutral-200 dark:border-neutral-700" />
              <Link to="/v2/workspace-settings" onClick={close} className={menuItemClass}>
                <div className={cn(iconBoxClass, "bg-primary-100 dark:bg-primary-900/30 text-primary-700 dark:text-primary-300")}><Building2 className="h-4 w-4" /></div>
                <div><p className="font-semibold">{t('workspace.title')}</p><p className="text-xs text-neutral-600 dark:text-neutral-400">{t('nav.teamAndBilling')}</p></div>
              </Link>
            </>
          )}
        </div>
        <div className="p-2 border-t border-neutral-200 dark:border-neutral-700 bg-neutral-50 dark:bg-neutral-800 rounded-b-xl">
          <button onClick={onLogout} className="flex items-center gap-3 w-full px-3 py-2.5 rounded-lg text-sm text-neutral-700 dark:text-neutral-300 hover:text-red-700 dark:hover:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/30 transition-colors">
            <div className={cn(iconBoxClass, "bg-neutral-200 dark:bg-neutral-700 text-neutral-600 dark:text-neutral-400")}><LogOut className="h-4 w-4" /></div>
            <span className="font-semibold">{t('common.logout')}</span>
          </button>
        </div>
      </div>
    </div>
  )
}
